import {
  RealtimeEventType,
  type RealtimeEventCbCancel,
  type FileInput,
  type RealtimeMember,
} from '@doxyme/realtime';
import {
  createAsyncThunk,
  createSlice,
  type PayloadAction,
} from '@reduxjs/toolkit';
import { type AppDispatch, type RootState } from '../../stores/redux/setup';
import { getLogger } from '../../libs/logger';
import {
  type ConnectPayload,
  type RealtimeSlice,
  type RealtimeState,
  type RealtimeOnMessageProps,
  type RealtimeSendMessageProps,
  type RealtimeSendMessageManyProps,
  type RealtimeSendMessageToAllIdentitiesProps,
  type JoinRoomsPayload,
} from './realtime.types';
import { initRealtimeLogger, throwNoMemberError } from './realtime.helpers';
import { subscribeToMessage } from './realtime.subscribe';
import { getAllRealtimeIdsByChatUserId } from './realtime.selectors';
import { ObservableAction, observe } from '../observability';
import { CustomDatadogContextKeys } from '../tracking/trackers/datadog/context.datadog';
import {
  removeRumGlobalContextProp,
  setRumGlobalContextProp,
} from '../tracking/trackers/datadog/rum.datadog';
import { RealtimeDebugger } from './realtimeDebugger.service';
import { realtimeInstance } from './realtime.instance';

export { realtimeInstance };

initRealtimeLogger();
const realtimeDebugger = new RealtimeDebugger();
const unsubscribeMessages: RealtimeEventCbCancel[] = [];
const unsubscribeEvents: RealtimeEventCbCancel[] = [];

const logger = getLogger('Realtime');

export const initialState: RealtimeSlice = {
  state: undefined,
  // TODO: rename to roomMembersMap
  membersMap: {},
  tenantMembersMap: {},
  joinedMembersIds: [],
  removedMembersIds: [],
  identity: undefined,
  isJoined: false,
  isLoading: false,
  picture: undefined,
  ackRequests: {},
};

export const joinRooms = createAsyncThunk(
  'realtime/joinRooms',
  async (payload: JoinRoomsPayload, api) => {
    const { role, roomIds, credentials, state } = payload;

    await realtimeInstance.join(role, roomIds, credentials ?? {}, state);
    api.dispatch(realtimeSlice.actions.setState(state));
    setRumGlobalContextProp(
      CustomDatadogContextKeys.RealtimeIdentityId,
      realtimeInstance.identity.id,
    );
  },
);

export const joinTenantChannel = createAsyncThunk(
  'realtime/joinTenantChannel',
  async () => {
    return realtimeInstance.joinTenantChannel();
  },
);

export const connect = createAsyncThunk(
  'realtime/connect',
  async (payload: ConnectPayload, api) => {
    const { role, roomIds } = payload;

    try {
      await api.dispatch(joinRooms(payload)).unwrap();

      const { listenForMediaStatusEvents } =
        await import('./realtime.mediaListeners');
      unsubscribeEvents.push(await listenForMediaStatusEvents(api.dispatch));

      return realtimeInstance.identity.id;
    } catch (error) {
      logger.error(
        '[realtime/connect] Join to realtime failed',
        {
          role,
          roomIds,
        },
        error,
      );

      throw error;
    }
  },
);

export const leave = createAsyncThunk(
  'realtime/leave',
  async (isLeavingPage: boolean, api) => {
    try {
      await realtimeInstance.leave(isLeavingPage);
      api.dispatch(realtimeSlice.actions.left());
    } catch (error) {
      logger.error('[realtime/leave]', error);
    } finally {
      removeRumGlobalContextProp(CustomDatadogContextKeys.RealtimeIdentityId);
    }
  },
);

export const initialize = createAsyncThunk<
  Promise<void>,
  undefined,
  { state: RootState; dispatch: AppDispatch }
>('realtime/initialize', async (_, api) => {
  if (unsubscribeEvents.length > 0) {
    return;
  }

  unsubscribeEvents.push(
    realtimeInstance.on(
      [RealtimeEventType.Join, RealtimeEventType.SetState],
      () => {
        const members = Object.values(realtimeInstance.membersMap);
        for (const member of members) {
          realtimeDebugger.patientJoin(member.identity);
        }

        api.dispatch(realtimeSlice.actions.otherParticipantJoined());
      },
    ),
    realtimeInstance.on([RealtimeEventType.Leave], (event) => {
      realtimeDebugger.patientLeft(event.from);

      api.dispatch(realtimeSlice.actions.otherParticipantLeft());
    }),
  );
});

export const sendMessage = createAsyncThunk(
  'realtime/sendMessage',
  async ({ id, message }: RealtimeSendMessageProps) => {
    const targetMember = realtimeInstance.membersMap[id];

    if (targetMember === undefined) {
      return throwNoMemberError(id, message, 'sendMessage');
    }

    return realtimeInstance.sendMessage(targetMember.identity, message);
  },
);

export const sendMessageMany = createAsyncThunk(
  'realtime/sendMessage',
  async ({ ids, message }: RealtimeSendMessageManyProps) => {
    const targetMembers = ids.map((realtimeId) => ({
      realtimeId,
      targetMember: realtimeInstance.membersMap[realtimeId],
    }));

    const missingMembers = targetMembers.filter(
      ({ targetMember }) => !targetMember,
    );
    if (missingMembers.length > 0) {
      const logIds = missingMembers
        .map(({ realtimeId }) => realtimeId)
        .join(', ');
      throwNoMemberError(logIds, message, 'sendMessageMany');
    }

    return Promise.allSettled(
      targetMembers.map(async (item) =>
        realtimeInstance.sendMessage(
          // it is safe to cast as missing member would throw an error above
          (item.targetMember as RealtimeMember).identity,
          message,
        ),
      ),
    );
  },
);

export const sendMessageToAllIdentities = createAsyncThunk<
  Promise<ReturnType<AppDispatch>>,
  RealtimeSendMessageToAllIdentitiesProps,
  { state: RootState; dispatch: AppDispatch }
>(
  'realtime/sendMessageToAllIdentities',
  async (
    { chatUserId, message }: RealtimeSendMessageToAllIdentitiesProps,
    { dispatch, getState },
  ) => {
    const identityIds = getAllRealtimeIdsByChatUserId(getState(), chatUserId);
    return dispatch(sendMessageMany({ ids: identityIds, message }));
  },
);

/**
 * @deprecated please use subscribeToMessage instead.
 */
export const onMessage = createAsyncThunk(
  'realtime/onMessage',
  (props: RealtimeOnMessageProps) => {
    const unsubscribe = subscribeToMessage(props);
    unsubscribeMessages.push(unsubscribe);
    return null;
  },
);

const setPicture = createAsyncThunk(
  'realtime/setPicture',
  async (payload: FileInput) => {
    try {
      await realtimeInstance.setPicture(payload);
    } catch (error) {
      logger.error('[realtime/setPicture]', error);
      throw error;
    }
  },
);

export const setProviderPicture = createAsyncThunk(
  'realtime/setProviderPicture',
  async (payload: string, api) => {
    if (!payload) {
      return;
    }

    try {
      await api.dispatch(setPicture({ url: payload })).unwrap();
      api.dispatch(realtimeSlice.actions.setPicture(payload));
    } catch {
      api.dispatch(realtimeSlice.actions.setPicture(undefined));
    }
  },
);

export const removeProviderPicture = createAsyncThunk(
  'realtime/removeProviderPicture',
  async (_, api) => {
    await api.dispatch(setPicture({ url: '' })).unwrap();
    api.dispatch(realtimeSlice.actions.setPicture(undefined));
  },
);

export const setPatientPicture = createAsyncThunk(
  'realtime/setPatientPicture',
  async (payload: ArrayBuffer, api) => {
    const data = {
      data: payload,
      name: `img-${realtimeInstance.identity.id}`,
    };
    return api.dispatch(setPicture(data));
  },
);

export const setRealtimeState = createAsyncThunk(
  'realtime/setRealtimeState',
  async (payload: RealtimeState, api) => {
    try {
      await realtimeInstance.setState(payload);
      api.dispatch(realtimeSlice.actions.setState(payload));
    } catch (stateError) {
      logger.error('[realtime/setState]', stateError);
    }
  },
);

export const updateRealtimeState = createAsyncThunk(
  'realtime/updateRealtimeState',
  async (payload: Partial<RealtimeState>, api) => {
    try {
      // Delegate the read-merge-write to the realtime client so the merge runs
      // after any in-flight join() and is serialized with concurrent
      // setState/updateState calls. Reading + merging in this thunk would race
      // with _join's _state assignment and clobber join-supplied fields.
      const merged = await realtimeInstance.updateState(payload);
      api.dispatch(realtimeSlice.actions.setState(merged));
    } catch (stateError) {
      logger.error('[realtime/updateState]', stateError);
    }
  },
);

export const unsubscribe = createAsyncThunk('realtime/unsubscribe', () => {
  for (const unsub of unsubscribeMessages) {
    unsub();
  }
  for (const unsub of unsubscribeEvents) {
    unsub();
  }
});

export const realtimeSlice = createSlice({
  name: 'realtime',
  initialState: initialState,
  reducers: {
    setState: (state, action: PayloadAction<RealtimeSlice['state']>) => {
      state.state = action.payload;
    },
    setPicture: (state, action: PayloadAction<string | undefined>) => {
      state.picture = action.payload;
    },
    removeParticipant: (state, action: PayloadAction<string>) => {
      const identityIdOfMemberToRemove = action.payload;
      state.removedMembersIds.push(identityIdOfMemberToRemove);
    },
    otherParticipantJoined: (state) => {
      state.membersMap = {
        // should we use selector here?
        ...state.membersMap,
        ...realtimeInstance.membersMap,
      };
      state.tenantMembersMap = {
        ...realtimeInstance.tenantMembersMap,
      };
      state.joinedMembersIds = Object.keys(realtimeInstance.membersMap);
    },
    otherParticipantLeft: (state) => {
      state.joinedMembersIds = Object.keys(realtimeInstance.membersMap);
      state.tenantMembersMap = {
        ...realtimeInstance.tenantMembersMap,
      };
    },
    left: (state) => {
      state.isJoined = false;
      state.identity = undefined;
    },
    ackRequested: (state, action: PayloadAction<Record<string, string>>) => {
      const {
        payload: { messageId },
      } = action;
      if (messageId !== undefined) {
        state.ackRequests[messageId] = Date.now();
      }
    },
    ackReceived: (state, action: PayloadAction<Record<string, string>>) => {
      const {
        payload: { messageId },
      } = action;
      if (messageId !== undefined) {
        const start = state.ackRequests[messageId];
        if (start === undefined) {
          logger.warn('[ackReceived] ack for unknown messageId received', {
            messageId,
          });
          return;
        }
        const ackRequests: Record<string, number> = {};
        for (const [k, v] of Object.entries(state.ackRequests)) {
          if (k !== messageId) {
            ackRequests[k] = v;
          }
        }
        state.ackRequests = ackRequests;

        const end: number = Date.now();
        const latency = (end - start) / 2;

        const payload = {
          latency,
          messageId,
        };
        logger.debug('[ackReceived]', payload);
        observe.action(ObservableAction.AckReceived, payload);
      }
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(connect.fulfilled, (state, _action) => {
        return {
          ...state,
          membersMap: realtimeInstance.membersMap,
          joinedMembersIds: Object.keys(realtimeInstance.membersMap),
          isJoined: true,
          identity: realtimeInstance.identity,
          isLoading: false,
        };
      })
      .addCase(connect.rejected, (state, action) => {
        return { ...state, error: action.error, isLoading: false };
      })
      .addCase(connect.pending, (state, _action) => {
        return { ...state, isLoading: true };
      });
  },
});
export const {
  otherParticipantJoined,
  otherParticipantLeft,
  ackReceived,
  ackRequested,
  removeParticipant,
  setState,
} = realtimeSlice.actions;
